"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  drawNumbers,
  isDrawableRange,
  rangeSize,
} from "@/lib/tool-kit/random-tools/number-draw";

const MIN_COUNT = 1;
const MAX_COUNT = 1000;

interface NumberChip {
  id: string;
  value: number;
}

function clampCount(raw: string): number {
  const parsed = parseIntegerInput(raw);
  if (parsed === null) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, parsed));
}

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_NUMBER_GENERATOR_STRINGS = {
  range: "Range",
  minimum: "Minimum",
  maximum: "Maximum",
  howMany11000: "How many (1–1,000)",
  options: "Options",
  allowDuplicates: "Allow duplicates",
  sortResults: "Sort results",
  generateNumbers: "Generate Numbers",
  copied: "Copied!",
  copyAll: "Copy All",
  yourRandomNumbers: "Your random numbers",
  numbersAreProducedWithYour:
    "Numbers come from your browser's cryptographic random source with rejection sampling, so every value in the range is equally likely — great for games, sampling, and raffles. For passwords and keys, use a dedicated password generator.",
  enterValidWholeNumbers: "Enter valid whole numbers for minimum and maximum.",
  minimumMustBeLessOrEqual: "Minimum must be less than or equal to maximum.",
  withoutDuplicatesRangeHoldsOnly:
    "Without duplicates, the range {min} to {max} holds only {count} {numberWord} — lower the quantity or widen the range.",
  numberWord: "one: number|other: numbers",
} as const;

export default function RandomNumberGeneratorWidget() {
  const [minInput, setMinInput] = useState("1");
  const [maxInput, setMaxInput] = useState("100");
  const [count, setCount] = useState(5);
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [sortResults, setSortResults] = useState(false);
  const [numbers, setNumbers] = useState<NumberChip[]>([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const locale = useToolLocale();
  const strings = useToolUi(RANDOM_NUMBER_GENERATOR_STRINGS);

  function handleGenerate() {
    const min = parseIntegerInput(minInput);
    const max = parseIntegerInput(maxInput);
    if (min === null || max === null || !isDrawableRange(min, max)) {
      setError(strings.enterValidWholeNumbers);
      setNumbers([]);
      return;
    }
    if (min > max) {
      setError(strings.minimumMustBeLessOrEqual);
      setNumbers([]);
      return;
    }
    const size = rangeSize(min, max);
    if (!allowDuplicates && count > size) {
      setError(
        t(strings.withoutDuplicatesRangeHoldsOnly, {
          min,
          max,
          count: size,
          numberWord: tPlural(strings.numberWord, locale, size),
        })
      );
      setNumbers([]);
      return;
    }
    setError("");
    let values = drawNumbers(min, max, count, !allowDuplicates);
    if (sortResults) {
      values = values.slice().sort((a, b) => a - b);
    }
    setNumbers(values.map((value) => ({ id: crypto.randomUUID(), value })));
    setCopied(false);
  }

  function handleCopyAll() {
    if (numbers.length === 0) {
      return;
    }
    const text = numbers.map((n) => n.value).join(", ");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Range */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.range}</p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="number-min"
            >
              {strings.minimum}
            </label>
            <input
              className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="number-min"
              onChange={(e) => setMinInput(e.target.value)}
              type="number"
              value={minInput}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="number-max"
            >
              {strings.maximum}
            </label>
            <input
              className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="number-max"
              onChange={(e) => setMaxInput(e.target.value)}
              type="number"
              value={maxInput}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="number-count"
            >
              {strings.howMany11000}
            </label>
            <input
              className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="number-count"
              max={MAX_COUNT}
              min={MIN_COUNT}
              onChange={(e) => setCount(clampCount(e.target.value))}
              type="number"
              value={count}
            />
          </div>
        </div>
      </section>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={allowDuplicates}
              className="accent-primary"
              onChange={(e) => setAllowDuplicates(e.target.checked)}
              type="checkbox"
            />
            {strings.allowDuplicates}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={sortResults}
              className="accent-primary"
              onChange={(e) => setSortResults(e.target.checked)}
              type="checkbox"
            />
            {strings.sortResults}
          </label>
        </div>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateNumbers}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={numbers.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
      </div>
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}
      {/* Results */}
      {numbers.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.yourRandomNumbers}
          </h2>
          <div className="flex flex-wrap gap-2">
            {numbers.map((n) => (
              <span
                className="rounded-full border border-border bg-card px-4 py-1.5 text-foreground text-sm"
                key={n.id}
              >
                {n.value}
              </span>
            ))}
          </div>
        </section>
      )}
      <p className="text-muted-foreground text-xs leading-relaxed">
        {strings.numbersAreProducedWithYour}
      </p>
    </>
  );
}
